import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  Divider,
  Chip,
  ListItemButton,
  ListItemContent,
  ModalClose,
  Modal,
  ModalDialog,
  Alert,
  Card,
  CardContent,
  Button,
} from '@mui/joy';
import {
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Menu as MenuIcon,
  SwitchAccount as SwitchAccountIcon,
  Badge as BadgeIcon,
} from '@mui/icons-material';
import { useAuth } from '../../main/context/AuthContext';
import { useSiteSettings } from '../../main/context/SiteSettingsContext';
import { getSiteDisplayName } from '../../main/utils/siteDisplayName';
import { useTranslation } from 'react-i18next';
import { setAppLanguage, resolveAppLanguage } from '../../../i18n';
import CustomerDrawer from './CustomerDrawer';
import { switchCustomerAccount, checkProfileEligibility, completeProfile } from '../../main/API/customer';
import { primaryColor } from '../../main/theme/constants';
import { Input, FormControl, FormLabel, FormHelperText } from '@mui/joy';
import { getLocalizedUserFullName, getLocalizedUserInitials } from '../../main/utils/userDisplayName';
import { translateApiErrorData, translateAggregateBackendMessage } from '../../main/utils/masterApiError';
import { dedupeRequest } from '../../main/utils/dedupeRequest';
import { filterActiveCustomerAccounts, isAccountInactiveApiResponse } from '../../main/utils/customerAccountApproval';

function isValidEmailFormat(value) {
  const s = String(value).trim();
  if (!s) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

const PROFILE_FIELD_MAP = {
  first_name: 'firstName',
  last_name: 'lastName',
  first_name_ar: 'firstNameAr',
  last_name_ar: 'lastNameAr',
  id_number: 'idNumber',
  email: 'email',
};

function parseProfileCompletionErrors(errorData, t) {
  if (!errorData || typeof errorData !== 'object') {
    return null;
  }
  if (errorData.error === 'Profile already completed') {
    return { kind: 'alreadyCompleted', message: t('customer.profileAlreadyCompleted') };
  }

  const validationErrors = {};
  Object.keys(errorData).forEach((key) => {
    if (Array.isArray(errorData[key]) && errorData[key].length > 0) {
      const raw = errorData[key][0];
      const formKey = PROFILE_FIELD_MAP[key] || key;
      if (key === 'email' && typeof raw === 'string' && raw.includes('already registered')) {
        validationErrors[formKey] = t('customer.emailAlreadyRegistered');
      } else {
        validationErrors[formKey] = translateAggregateBackendMessage(String(raw), t);
      }
    }
  });

  if (Object.keys(validationErrors).length > 0) {
    return { kind: 'validation', validationErrors };
  }

  return {
    kind: 'generic',
    message: translateApiErrorData(errorData, t) || t('customer.profileCompleteFailed'),
  };
}

export default function CustomerAppBar({ restrictNavToHome = false }) {
  const { t, i18n } = useTranslation('ui');
  const currentLang = resolveAppLanguage(i18n.language);
  const { user, logout, getAllUserRoles } = useAuth();
  const { settings } = useSiteSettings();
  const siteTitle = getSiteDisplayName(settings, i18n.language);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showSwitchModal, setShowSwitchModal] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState(null);
  const [profileFormData, setProfileFormData] = useState({
    firstName: '',
    lastName: '',
    firstNameAr: '',
    lastNameAr: '',
    idNumber: '',
    email: '',
  });
  const [formErrors, setFormErrors] = useState({});

  // Get user data with fallback to localStorage
  const getUserData = () => {
    if (user) return user;
    
    const storedUserData = localStorage.getItem('user_data');
    if (storedUserData) {
      try {
        return JSON.parse(storedUserData);
      } catch (e) {
        console.error('Error parsing stored user data:', e);
      }
    }
    return null;
  };

  const userData = getUserData();
  const displayName =
    getLocalizedUserFullName(userData, i18n.language) || (userData && userData.username) || t('actions.user');
  const userRoles = getAllUserRoles();

  // Handle open switch account modal
  const handleOpenSwitchAccount = useCallback(() => {
    const storedAccounts = localStorage.getItem('customer_accounts_list');
    if (storedAccounts) {
      try {
        const parsedAccounts = filterActiveCustomerAccounts(JSON.parse(storedAccounts));
        setAccounts(parsedAccounts);
        localStorage.setItem('customer_accounts_list', JSON.stringify(parsedAccounts));
      } catch (e) {
        console.error('Error parsing stored accounts:', e);
      }
    }
    setShowSwitchModal(true);
    setError(null);
    setMessage(null);
    setUserMenuOpen(false); // Close user menu when opening switch account modal
  }, []);

  // Check profile eligibility on mount
  useEffect(() => {
    const checkProfile = async () => {
      const accessToken = localStorage.getItem('access_token');
      if (!accessToken) return;

      try {
        setProfileLoading(true);
        const response = await dedupeRequest(
          'customer/profile-eligibility',
          () => checkProfileEligibility()
        );
        
        if (response && response.status === 200) {
          const data = response.data;

          // If eligible, show modal and pre-fill existing data
          if (data.eligible) {
            setShowProfileModal(true);
            const u = data.user;
            setProfileFormData({
              firstName: (u && u.first_name) || '',
              lastName: (u && u.last_name) || '',
              firstNameAr: (u && u.first_name_ar) || '',
              lastNameAr: (u && u.last_name_ar) || '',
              idNumber: (u && u.id_number) || '',
              email: u && u.email != null && String(u.email).trim() !== '' ? String(u.email).trim() : '',
            });
          } else {
            setShowProfileModal(false);
          }
        }
      } catch (err) {
        console.error('Error checking profile eligibility:', err);
      } finally {
        setProfileLoading(false);
      }
    };

    // Small delay to ensure user data is loaded
    const timer = setTimeout(() => {
      checkProfile();
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Load stored accounts list on mount
  useEffect(() => {
    const storedAccounts = localStorage.getItem('customer_accounts_list');
    if (storedAccounts) {
      try {
        const parsedAccounts = filterActiveCustomerAccounts(JSON.parse(storedAccounts));
        setAccounts(parsedAccounts);
        localStorage.setItem('customer_accounts_list', JSON.stringify(parsedAccounts));
      } catch (e) {
        console.error('Error parsing stored accounts:', e);
      }
    }

    // Check if switch account modal should be opened (from drawer)
    const shouldOpenModal = localStorage.getItem('open_switch_account_modal');
    if (shouldOpenModal === 'true') {
      handleOpenSwitchAccount();
      localStorage.removeItem('open_switch_account_modal');
    }
  }, [handleOpenSwitchAccount]);

  // Handle switch account (no OTP required)
  const handleSwitchAccount = async (userId) => {
    setLoading(true);
    setError(null);
    setMessage(null);

    // Get phone number from current user data
    const currentUserPhone = userData && userData.phone_number 
      ? userData.phone_number 
      : null;

    if (!currentUserPhone) {
      setError(t('customerAppBar.phoneMissing'));
      setLoading(false);
      return;
    }

    try {
      const response = await switchCustomerAccount(userId, currentUserPhone);

      if (response && response.status === 200) {
        const data = response.data;

        // Store tokens
        localStorage.setItem("access_token", data.access);
        localStorage.setItem("refresh_token", data.refresh);
        localStorage.setItem("Authorization", `Bearer ${data.access}`);

        // Store user data
        localStorage.setItem("user_data", JSON.stringify(data.user));
        if (data.user.roles && Array.isArray(data.user.roles)) {
          const roleNames = data.user.roles.map((role) => role.name);
          localStorage.setItem("user_roles", JSON.stringify(roleNames));
        }

        setMessage(t('customerAppBar.switchSuccess'));

        // Reload page to update auth context
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        const errorData = (response && response.data) || {};
        setError(translateApiErrorData(errorData, t) || t('customerAuth.errSwitchAccountFailed'));
      }
    } catch (err) {
      console.error("Error switching account:", err);
      const status = err && err.response && err.response.status;
      const errorData = (err && err.response && err.response.data) || {};
      if (isAccountInactiveApiResponse(status, errorData)) {
        setError(
          translateApiErrorData(errorData, t) || t('customerAuth.accountInactiveDefaultBody')
        );
      } else {
        setError(translateApiErrorData(errorData, t) || t('backendErrors.network'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUserMenuOpen = () => {
    setUserMenuOpen(true);
  };

  const handleUserMenuClose = () => {
    setUserMenuOpen(false);
  };

  const handleLogout = () => {
    handleUserMenuClose();
    logout();
  };

  // Handle profile form input change
  const handleProfileInputChange = (field, value) => {
    setProfileFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Handle profile completion submission
  const handleCompleteProfile = async () => {
    setProfileError(null);
    setFormErrors({});

    // Validate form
    const errors = {};
    if (!profileFormData.firstName.trim()) {
      errors.firstName = t('customerManager.firstNameRequired');
    }
    if (!profileFormData.lastName.trim()) {
      errors.lastName = t('customerManager.lastNameRequired');
    }
    if (!profileFormData.firstNameAr.trim()) {
      errors.firstNameAr = t('customerManager.firstNameArRequired');
    }
    if (!profileFormData.lastNameAr.trim()) {
      errors.lastNameAr = t('customerManager.lastNameArRequired');
    }
    if (!profileFormData.idNumber.trim()) {
      errors.idNumber = t('customerManager.idNumberRequired');
    }
    if (!profileFormData.email.trim()) {
      errors.email = t('customer.emailRequired');
    } else if (!isValidEmailFormat(profileFormData.email)) {
      errors.email = t('customer.emailInvalid');
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      setProfileLoading(true);
      const response = await completeProfile(profileFormData);

      if (response && response.status === 200) {
        // Update user data in localStorage
        if (response.data.user) {
          localStorage.setItem('user_data', JSON.stringify(response.data.user));
        }

        // Close modal and reload to update UI
        setShowProfileModal(false);
        
        // Reload page to update auth context
        setTimeout(() => {
          window.location.reload();
        }, 500);
      } else {
        const parsed = parseProfileCompletionErrors((response && response.data) || {}, t);
        if (parsed?.kind === 'alreadyCompleted') {
          setProfileError(parsed.message);
          setShowProfileModal(false);
        } else if (parsed?.kind === 'validation') {
          setFormErrors(parsed.validationErrors);
        } else if (parsed?.kind === 'generic') {
          setProfileError(parsed.message);
        }
      }
    } catch (err) {
      console.error('Error completing profile:', err);
      const parsed = parseProfileCompletionErrors(err.response?.data, t);
      if (parsed?.kind === 'alreadyCompleted') {
        setProfileError(parsed.message);
        setShowProfileModal(false);
      } else if (parsed?.kind === 'validation') {
        setFormErrors(parsed.validationErrors);
      } else if (parsed?.kind === 'generic') {
        setProfileError(parsed.message);
      } else {
        setProfileError(t('customer.profileNetworkError'));
      }
    } finally {
      setProfileLoading(false);
    }
  };

  const getInitials = () => (userData ? getLocalizedUserInitials(userData, i18n.language) : 'C');

  return (
    <>
      {/* AppBar */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: 64,
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          zIndex: 1200,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingX: 3,
          paddingY: 0,
        }}
      >
        {/* Left side - Drawer Button */}
        <IconButton
          variant="plain"
          onClick={() => setDrawerOpen(true)}
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(229, 231, 235, 0.5)',
            borderRadius: '12px',
            width: 44,
            height: 44,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
            '&:hover': {
              backgroundColor: 'rgba(249, 250, 251, 0.9)',
              borderColor: 'rgba(209, 213, 219, 0.6)',
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.06)',
              transform: 'scale(1.02)',
            },
            '&:active': {
              transform: 'scale(0.98)',
            },
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          <MenuIcon 
            sx={{ 
              color: '#667eea',
              fontSize: 22,
              transition: 'transform 0.2s ease',
            }} 
          />
        </IconButton>

        {/* Center - site name from settings */}
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', minWidth: 0 }}>
          <Typography
            level="title-md"
            sx={{
              fontWeight: 700,
              color: '#111827',
              letterSpacing: '-0.02em',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              px: 1,
            }}
          >
            {siteTitle}
          </Typography>
        </Box>

        {/* Right side - Language + User Profile */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            role="group"
            aria-label={t('language.languageToggle')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.25,
              p: 0.25,
              borderRadius: '10px',
              border: '1px solid #e5e7eb',
              backgroundColor: '#f9fafb',
            }}
          >
            <Button
              size="sm"
              variant={currentLang === 'en' ? 'solid' : 'plain'}
              color={currentLang === 'en' ? 'primary' : 'neutral'}
              onClick={() => setAppLanguage('en')}
              sx={{ minWidth: 40, fontWeight: 600, fontSize: '12px', py: 0.25, px: 0.75 }}
            >
              {t('language.english')}
            </Button>
            <Button
              size="sm"
              variant={currentLang === 'zh' ? 'solid' : 'plain'}
              color={currentLang === 'zh' ? 'primary' : 'neutral'}
              onClick={() => setAppLanguage('zh')}
              sx={{ minWidth: 40, fontWeight: 600, fontSize: '12px', py: 0.25, px: 0.75 }}
            >
              {t('language.chinese')}
            </Button>
            <Button
              size="sm"
              variant={currentLang === 'ar' ? 'solid' : 'plain'}
              color={currentLang === 'ar' ? 'primary' : 'neutral'}
              onClick={() => setAppLanguage('ar')}
              sx={{ minWidth: 40, fontWeight: 600, fontSize: '12px', py: 0.25, px: 0.75 }}
            >
              {t('language.arabic')}
            </Button>
          </Box>
          {/* User menu: anchor dropdown to trigger only so RTL/LTR alignment matches viewport edge */}
          <Box sx={{ position: 'relative' }}>
          {/* User Info */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              paddingX: 1.5,
              paddingY: 0.75,
              borderRadius: '10px',
              cursor: 'pointer',
              backgroundColor: 'transparent',
              border: '1px solid transparent',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: '#f9fafb',
                borderColor: '#e5e7eb',
              },
            }}
            onClick={handleUserMenuOpen}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                backgroundColor: '#f3f4f6',
                border: '1px solid #e5e7eb',
                fontSize: '14px',
                fontWeight: 600,
                color: '#667eea',
                boxShadow: 'none',
              }}
            >
              {getInitials()}
            </Avatar>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <Typography 
                level="body-sm" 
                sx={{ 
                  fontWeight: 600, 
                  color: '#111827',
                  fontSize: '14px',
                  lineHeight: 1.2,
                }}
              >
                {displayName}
              </Typography>
              {userRoles && userRoles.length > 0 && (
                <Typography 
                  level="body-xs" 
                  sx={{ 
                    color: '#6b7280', 
                    fontSize: '11px',
                    lineHeight: 1.2,
                    mt: 0.25,
                  }}
                >
                  {userRoles[0].replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </Typography>
              )}
            </Box>
            <ArrowDropDownIcon 
              sx={{ 
                fontSize: 20, 
                color: '#6b7280',
                transition: 'transform 0.2s ease',
                transform: userMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              }} 
            />
          </Box>

          {/* User Menu Dropdown */}
          {userMenuOpen && (
            <>
              {/* Backdrop */}
              <Box
                onClick={handleUserMenuClose}
                sx={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 1299,
                }}
              />
              {/* Dropdown Menu */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '100%',
                  insetInlineEnd: 0,
                  mt: 1,
                  width: { xs: 'min(90vw, 400px)', sm: 400 },
                  maxWidth: 'min(400px, calc(100vw - 24px))',
                  maxHeight: '85vh',
                  overflow: 'auto',
                  backgroundColor: '#ffffff',
                  borderRadius: '12px',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                  border: '1px solid #e5e7eb',
                  zIndex: 1300,
                  animation: 'slideDown 0.2s ease-out',
                  '@keyframes slideDown': {
                    from: {
                      opacity: 0,
                      transform: 'translateY(-10px)',
                    },
                    to: {
                      opacity: 1,
                      transform: 'translateY(0)',
                    },
                  },
                }}
              >
              
              {/* Profile Header */}
              <Box 
                sx={{ 
                  p: 3, 
                  backgroundColor: '#f9fafb',
                  borderTopLeftRadius: '12px',
                  borderTopRightRadius: '12px',
                  borderBottom: '1px solid #e5e7eb',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Avatar
                    sx={{
                      width: 56,
                      height: 56,
                      backgroundColor: '#f3f4f6',
                      border: '2px solid #e5e7eb',
                      fontSize: '20px',
                      fontWeight: 600,
                      color: '#667eea',
                      boxShadow: 'none',
                    }}
                  >
                    {getInitials()}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography 
                      level="title-md" 
                      sx={{ 
                        fontWeight: 600, 
                        color: '#111827', 
                        mb: 0.5,
                        fontSize: '16px',
                      }}
                    >
                      {displayName}
                    </Typography>
                    {userData && userData.email && (
                      <Typography 
                        level="body-sm" 
                        sx={{ 
                          color: '#6b7280', 
                          fontSize: '13px',
                        }}
                      >
                        {userData.email}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Roles */}
                {userRoles && userRoles.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {userRoles.map((role, index) => (
                      <Chip
                        key={index}
                        size="sm"
                        variant="soft"
                        sx={{ 
                          fontSize: '11px',
                          backgroundColor: '#ede9fe',
                          color: '#667eea',
                          border: 'none',
                          fontWeight: 500,
                        }}
                      >
                        {role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </Chip>
                    ))}
                  </Box>
                )}
              </Box>

              {/* Switch Account Button */}
              {accounts.length > 0 && (
                <Box sx={{ p: 2, backgroundColor: '#ffffff', borderBottom: '1px solid #e5e7eb' }}>
                  <ListItemButton
                    onClick={handleOpenSwitchAccount}
                    sx={{
                      borderRadius: '8px',
                      p: 1.25,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      flexDirection: 'row',
                      backgroundColor: 'transparent',
                      color: primaryColor,
                      border: `1px solid ${primaryColor}33`,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: `${primaryColor}10`,
                        borderColor: primaryColor,
                      },
                    }}
                  >
                    <SwitchAccountIcon sx={{ fontSize: 20, flexShrink: 0 }} />
                    <ListItemContent>
                      <Typography level="body-sm" sx={{ fontWeight: 500, fontSize: '14px' }}>
                        {t('customer.switchAccount')}
                      </Typography>
                    </ListItemContent>
                  </ListItemButton>
                </Box>
              )}

              {/* Account Details */}
              <Box sx={{ p: 3, backgroundColor: '#ffffff' }}>
                <Typography
                  level="body-xs"
                  sx={{
                    fontWeight: 600,
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    mb: 2,
                    fontSize: '11px',
                  }}
                >
                  {t('userMenu.accountInformation')}
                </Typography>

                {/* User ID */}
                {userData && userData.id && (
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1.5, 
                      mb: 1.5,
                      p: 1.5,
                      borderRadius: '8px',
                      backgroundColor: '#f9fafb',
                      border: '1px solid #f3f4f6',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: '#f3f4f6',
                        borderColor: '#e5e7eb',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '8px',
                        backgroundColor: '#fef3c7',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <BadgeIcon sx={{ fontSize: 18, color: '#f59e0b' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography level="body-xs" sx={{ color: '#6b7280', fontSize: '11px', fontWeight: 500, mb: 0.5 }}>
                        {t('userMenu.userId')}
                      </Typography>
                      <Typography level="body-sm" sx={{ color: '#111827', fontWeight: 500, fontSize: '14px' }}>
                        {userData.id}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Username */}
                {userData && userData.username && (
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1.5, 
                      mb: 1.5,
                      p: 1.5,
                      borderRadius: '8px',
                      backgroundColor: '#f9fafb',
                      border: '1px solid #f3f4f6',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: '#f3f4f6',
                        borderColor: '#e5e7eb',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '8px',
                        backgroundColor: '#e0e7ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <PersonIcon sx={{ fontSize: 18, color: '#667eea' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography level="body-xs" sx={{ color: '#6b7280', fontSize: '11px', fontWeight: 500, mb: 0.5 }}>
                        {t('userMenu.username')}
                      </Typography>
                      <Typography level="body-sm" sx={{ color: '#111827', fontWeight: 500, fontSize: '14px' }}>
                        {userData.username}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Email */}
                {userData && userData.email && (
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1.5, 
                      mb: 1.5,
                      p: 1.5,
                      borderRadius: '8px',
                      backgroundColor: '#f9fafb',
                      border: '1px solid #f3f4f6',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: '#f3f4f6',
                        borderColor: '#e5e7eb',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '8px',
                        backgroundColor: '#d1fae5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <EmailIcon sx={{ fontSize: 18, color: '#10b981' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography level="body-xs" sx={{ color: '#6b7280', fontSize: '11px', fontWeight: 500, mb: 0.5 }}>
                        {t('userMenu.email')}
                      </Typography>
                      <Typography level="body-sm" sx={{ color: '#111827', fontWeight: 500, fontSize: '14px' }}>
                        {userData.email}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Phone */}
                {userData && userData.phone_number && (
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1.5, 
                      mb: 1.5,
                      p: 1.5,
                      borderRadius: '8px',
                      backgroundColor: '#f9fafb',
                      border: '1px solid #f3f4f6',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: '#f3f4f6',
                        borderColor: '#e5e7eb',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '8px',
                        backgroundColor: '#fce7f3',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <PhoneIcon sx={{ fontSize: 18, color: '#ec4899' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography level="body-xs" sx={{ color: '#6b7280', fontSize: '11px', fontWeight: 500, mb: 0.5 }}>
                        {t('userMenu.phone')}
                      </Typography>
                      <Typography level="body-sm" sx={{ color: '#111827', fontWeight: 500, fontSize: '14px' }}>
                        {userData.phone_number}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Full Name */}
                {((userData && userData.first_name) ||
                  (userData && userData.last_name) ||
                  (userData && userData.first_name_ar) ||
                  (userData && userData.last_name_ar)) && (
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1.5, 
                      mb: 1.5,
                      p: 1.5,
                      borderRadius: '8px',
                      backgroundColor: '#f9fafb',
                      border: '1px solid #f3f4f6',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: '#f3f4f6',
                        borderColor: '#e5e7eb',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '8px',
                        backgroundColor: '#fed7aa',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <AccountCircleIcon sx={{ fontSize: 18, color: '#f97316' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography level="body-xs" sx={{ color: '#6b7280', fontSize: '11px', fontWeight: 500, mb: 0.5 }}>
                        {t('userMenu.fullName')}
                      </Typography>
                      <Typography level="body-sm" sx={{ color: '#111827', fontWeight: 500, fontSize: '14px' }}>
                        {getLocalizedUserFullName(userData, i18n.language) || t('actions.na')}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 0 }} />

              {/* Actions */}
              <Box sx={{ p: 2, backgroundColor: '#ffffff', borderTop: '1px solid #e5e7eb' }}>
                <ListItemButton
                  onClick={handleLogout}
                  sx={{
                    borderRadius: '8px',
                    p: 1.25,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    flexDirection: 'row',
                    backgroundColor: 'transparent',
                    color: '#dc2626',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: '#fef2f2',
                    },
                  }}
                >
                  <LogoutIcon sx={{ fontSize: 20, flexShrink: 0, transition: 'color 0.2s ease' }} />
                  <ListItemContent>
                    <Typography level="body-sm" sx={{ fontWeight: 500, fontSize: '14px' }}>
                      {t('actions.logout')}
                    </Typography>
                  </ListItemContent>
                </ListItemButton>
              </Box>
              </Box>
            </>
          )}
          </Box>
        </Box>
      </Box>

      {/* Navigation Drawer */}
      <CustomerDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} restrictNavToHome={restrictNavToHome} />

      {/* Switch Account Modal */}
      <Modal open={showSwitchModal} onClose={() => setShowSwitchModal(false)}>
        <ModalDialog
          sx={{
            maxWidth: 500,
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
          }}
        >
          <ModalClose />
          <Typography level="h3" sx={{ marginBottom: 2 }}>
            {t('customer.switchAccount')}
          </Typography>

          <Box>
            <Typography level="body2" sx={{ marginBottom: 2, color: 'text.secondary' }}>
              {t('customerAppBar.switchModalBody')}
            </Typography>
            {error && <Alert color="danger" sx={{ marginBottom: 2 }}>{error}</Alert>}
            {message && <Alert color="success" sx={{ marginBottom: 2 }}>{message}</Alert>}
            
            {accounts.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {accounts.map((account) => {
                  const isCurrentAccount = userData && userData.id === account.id;
                  return (
                    <Card 
                      key={account.id} 
                      sx={{ 
                        border: isCurrentAccount ? `2px solid ${primaryColor}` : '1px solid #e0e0e0',
                        backgroundColor: isCurrentAccount ? `${primaryColor}10` : 'white',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          borderColor: primaryColor,
                          boxShadow: `0 2px 8px ${primaryColor}33`,
                        },
                      }}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography level="title-md" sx={{ fontWeight: 'bold', marginBottom: 0.5 }}>
                              {account.first_name} {account.last_name}
                            </Typography>
                            {account.first_name_ar && account.last_name_ar && (
                              <Typography level="body3" sx={{ color: 'text.secondary', marginBottom: 0.5 }}>
                                {account.first_name_ar} {account.last_name_ar}
                              </Typography>
                            )}
                            {account.email && (
                              <Typography level="body3" sx={{ color: 'text.secondary', marginBottom: 0.5 }}>
                                {account.email}
                              </Typography>
                            )}
                            {account.id_number && (
                              <Typography level="body3" sx={{ color: 'text.secondary' }}>
                                {t('customerAuth.idPrefix', { id: account.id_number })}
                              </Typography>
                            )}
                          </Box>
                          {isCurrentAccount ? (
                            <Typography level="body2" sx={{ color: primaryColor, fontWeight: 'bold' }}>
                              {t('customerAppBar.currentAccount')}
                            </Typography>
                          ) : (
                            <Button
                              onClick={() => handleSwitchAccount(account.id)}
                              disabled={loading}
                              sx={{ backgroundColor: primaryColor }}
                            >
                              {loading ? t('customerAppBar.switching') : t('customerAppBar.switchAction')}
                            </Button>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            ) : (
              <Alert color="warning" sx={{ marginBottom: 2 }}>
                {t('customerAppBar.noOtherAccounts')}
              </Alert>
            )}
          </Box>
        </ModalDialog>
      </Modal>

      {/* Profile Completion Modal */}
      <Modal 
        open={showProfileModal} 
        onClose={() => {}}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 1, sm: 2 },
        }}
      >
        <ModalDialog
          sx={{
            maxWidth: { xs: '100%', sm: 600 },
            width: '100%',
            maxHeight: { xs: '90vh', sm: 'none' },
            overflow: { xs: 'auto', sm: 'visible' },
            borderRadius: { xs: '12px', sm: '16px' },
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            p: { xs: 2, sm: 3 },
            border: '1px solid #e5e7eb',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header - Light design without background */}
          <Box sx={{ mb: { xs: 2.5, sm: 3 }, flexShrink: 0 }}>
            <Typography 
              level="h3" 
              sx={{ 
                fontWeight: 600, 
                fontSize: { xs: '20px', sm: '24px' },
                mb: 1,
                color: '#111827',
              }}
            >
              {t('customer.completeProfile')}
            </Typography>
            <Typography 
              level="body-sm" 
              sx={{ 
                color: '#6b7280',
                fontSize: { xs: '13px', sm: '14px' },
                lineHeight: 1.5,
              }}
            >
              {t('customerAppBar.profileModalSubtitle')}
            </Typography>

            {accounts.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <ListItemButton
                  onClick={handleOpenSwitchAccount}
                  sx={{
                    borderRadius: '8px',
                    p: 1.25,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    flexDirection: 'row',
                    backgroundColor: 'transparent',
                    color: primaryColor,
                    border: `1px solid ${primaryColor}33`,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: `${primaryColor}10`,
                      borderColor: primaryColor,
                    },
                  }}
                >
                  <SwitchAccountIcon sx={{ fontSize: 20, flexShrink: 0 }} />
                  <ListItemContent>
                    <Typography level="body-sm" sx={{ fontWeight: 500, fontSize: '14px' }}>
                      {t('customer.switchAccount')}
                    </Typography>
                  </ListItemContent>
                </ListItemButton>
              </Box>
            )}
          </Box>

          {profileError && (
            <Alert color="danger" sx={{ mb: { xs: 2, sm: 2.5 }, borderRadius: '8px', flexShrink: 0 }}>
              {profileError}
            </Alert>
          )}

          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: { xs: 2.5, sm: 2.5 },
            flex: 1,
            overflow: { xs: 'visible', sm: 'visible' },
          }}>
            {/* English Name Fields */}
            <Box>
              <Typography 
                level="title-sm" 
                sx={{ 
                  mb: { xs: 1, sm: 1.5 }, 
                  fontWeight: 600,
                  color: '#111827',
                  fontSize: { xs: '13px', sm: '14px' },
                }}
              >
                {t('customerAppBar.englishNameSection')}
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1.5, sm: 2 } 
              }}>
                <FormControl error={!!formErrors.firstName} sx={{ flex: 1 }}>
                  <FormLabel sx={{ 
                    fontSize: { xs: '12px', sm: '13px' }, 
                    fontWeight: 500, 
                    mb: 0.5,
                    color: '#374151',
                  }}>
                    {`${t('customerManager.labelFirstName')} *`}
                  </FormLabel>
                  <Input
                    placeholder={t('customer.enterFirstName')}
                    value={profileFormData.firstName}
                    onChange={(e) => handleProfileInputChange('firstName', e.target.value)}
                    sx={{
                      borderRadius: '8px',
                      borderColor: '#d1d5db',
                      '&:focus-within': {
                        borderColor: primaryColor,
                        boxShadow: `0 0 0 3px ${primaryColor}15`,
                      },
                    }}
                  />
                  {formErrors.firstName && (
                    <FormHelperText sx={{ fontSize: '11px', mt: 0.5 }}>
                      {formErrors.firstName}
                    </FormHelperText>
                  )}
                </FormControl>
                <FormControl error={!!formErrors.lastName} sx={{ flex: 1 }}>
                  <FormLabel sx={{ 
                    fontSize: { xs: '12px', sm: '13px' }, 
                    fontWeight: 500, 
                    mb: 0.5,
                    color: '#374151',
                  }}>
                    {`${t('customerManager.labelLastName')} *`}
                  </FormLabel>
                  <Input
                    placeholder={t('customer.enterLastName')}
                    value={profileFormData.lastName}
                    onChange={(e) => handleProfileInputChange('lastName', e.target.value)}
                    sx={{
                      borderRadius: '8px',
                      borderColor: '#d1d5db',
                      '&:focus-within': {
                        borderColor: primaryColor,
                        boxShadow: `0 0 0 3px ${primaryColor}15`,
                      },
                    }}
                  />
                  {formErrors.lastName && (
                    <FormHelperText sx={{ fontSize: '11px', mt: 0.5 }}>
                      {formErrors.lastName}
                    </FormHelperText>
                  )}
                </FormControl>
              </Box>
            </Box>

            {/* Arabic Name Fields */}
            <Box>
              <Typography 
                level="title-sm" 
                sx={{ 
                  mb: { xs: 1, sm: 1.5 }, 
                  fontWeight: 600,
                  color: '#111827',
                  fontSize: { xs: '13px', sm: '14px' },
                }}
              >
                {t('customerAppBar.arabicNameSection')}
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1.5, sm: 2 } 
              }}>
                <FormControl error={!!formErrors.firstNameAr} sx={{ flex: 1 }}>
                  <FormLabel sx={{ 
                    fontSize: { xs: '12px', sm: '13px' }, 
                    fontWeight: 500, 
                    mb: 0.5,
                    color: '#374151',
                  }}>
                    {`${t('customerManager.labelFirstNameAr')} *`}
                  </FormLabel>
                  <Input
                    placeholder={t('customerAppBar.placeholderFirstNameAr')}
                    value={profileFormData.firstNameAr}
                    onChange={(e) => handleProfileInputChange('firstNameAr', e.target.value)}
                    dir="rtl"
                    sx={{
                      borderRadius: '8px',
                      borderColor: '#d1d5db',
                      '&:focus-within': {
                        borderColor: primaryColor,
                        boxShadow: `0 0 0 3px ${primaryColor}15`,
                      },
                    }}
                  />
                  {formErrors.firstNameAr && (
                    <FormHelperText sx={{ fontSize: '11px', mt: 0.5 }}>
                      {formErrors.firstNameAr}
                    </FormHelperText>
                  )}
                </FormControl>
                <FormControl error={!!formErrors.lastNameAr} sx={{ flex: 1 }}>
                  <FormLabel sx={{ 
                    fontSize: { xs: '12px', sm: '13px' }, 
                    fontWeight: 500, 
                    mb: 0.5,
                    color: '#374151',
                  }}>
                    {`${t('customerManager.labelLastNameAr')} *`}
                  </FormLabel>
                  <Input
                    placeholder={t('customerAppBar.placeholderLastNameAr')}
                    value={profileFormData.lastNameAr}
                    onChange={(e) => handleProfileInputChange('lastNameAr', e.target.value)}
                    dir="rtl"
                    sx={{
                      borderRadius: '8px',
                      borderColor: '#d1d5db',
                      '&:focus-within': {
                        borderColor: primaryColor,
                        boxShadow: `0 0 0 3px ${primaryColor}15`,
                      },
                    }}
                  />
                  {formErrors.lastNameAr && (
                    <FormHelperText sx={{ fontSize: '11px', mt: 0.5 }}>
                      {formErrors.lastNameAr}
                    </FormHelperText>
                  )}
                </FormControl>
              </Box>
            </Box>

            {/* Civil ID */}
            <FormControl error={!!formErrors.idNumber}>
              <FormLabel sx={{ 
                fontSize: { xs: '12px', sm: '13px' }, 
                fontWeight: 500, 
                mb: 0.5,
                color: '#374151',
              }}>
                {`${t('customerAppBar.civilIdLabel')} *`}
              </FormLabel>
              <Input
                placeholder={t('customer.enterCivilIdNumber')}
                value={profileFormData.idNumber}
                onChange={(e) => handleProfileInputChange('idNumber', e.target.value)}
                sx={{
                  borderRadius: '8px',
                  borderColor: '#d1d5db',
                  '&:focus-within': {
                    borderColor: primaryColor,
                    boxShadow: `0 0 0 3px ${primaryColor}15`,
                  },
                }}
              />
              {formErrors.idNumber && (
                <FormHelperText sx={{ fontSize: '11px', mt: 0.5 }}>
                  {formErrors.idNumber}
                </FormHelperText>
              )}
            </FormControl>

            <Box>
              <Typography
                level="title-sm"
                sx={{
                  mb: { xs: 1, sm: 1.5 },
                  fontWeight: 600,
                  color: '#111827',
                  fontSize: { xs: '13px', sm: '14px' },
                }}
              >
                {t('customerAppBar.emailSection')}
              </Typography>
              <FormControl error={!!formErrors.email}>
                <FormLabel sx={{
                  fontSize: { xs: '12px', sm: '13px' },
                  fontWeight: 500,
                  mb: 0.5,
                  color: '#374151',
                }}
                >
                  {`${t('customer.labelEmail')} *`}
                </FormLabel>
                <Input
                  type="email"
                  autoComplete="email"
                  placeholder={t('customer.enterEmail')}
                  value={profileFormData.email}
                  onChange={(e) => handleProfileInputChange('email', e.target.value)}
                  sx={{
                    borderRadius: '8px',
                    borderColor: '#d1d5db',
                    '&:focus-within': {
                      borderColor: primaryColor,
                      boxShadow: `0 0 0 3px ${primaryColor}15`,
                    },
                  }}
                />
                {formErrors.email && (
                  <FormHelperText sx={{ fontSize: '11px', mt: 0.5 }}>
                    {formErrors.email}
                  </FormHelperText>
                )}
              </FormControl>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, mt: { xs: 1, sm: 2 } }}>
              <Button
                onClick={handleCompleteProfile}
                disabled={profileLoading}
                loading={profileLoading}
                sx={{
                  flex: 1,
                  backgroundColor: primaryColor,
                  borderRadius: '8px',
                  py: { xs: 1.25, sm: 1.5 },
                  fontSize: { xs: '14px', sm: '15px' },
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#5f09c2',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                {profileLoading ? t('customerAppBar.completingProfile') : t('customer.completeProfile')}
              </Button>
            </Box>
          </Box>
        </ModalDialog>
      </Modal>
    </>
  );
}

CustomerAppBar.propTypes = {
  restrictNavToHome: PropTypes.bool,
};

